import * as RadixSlider from '@radix-ui/react-slider'
import { useMemo, useState } from 'react'

import type { RangeProps } from './range.types'

import { Box } from '../box'
import { styled, useStyleConfig, vui } from '../core'
import { Tooltip } from '../tooltip'
import { cs, filterUndefined } from '../utils'
import { rangeColors } from './consts'

const SliderRoot = styled(Box)<{ $disabled?: boolean }>`
  align-items: center;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  position: relative;
  touch-action: none;
  user-select: none;
  width: 100%;
`

const SliderTrack = styled(Box)`
  flex-grow: 1;
  height: 4px;
  position: relative;
`

const SliderRange = styled(Box)`
  height: 100%;
  position: absolute;
`

const SliderThumb = styled(Box)<{ $disabled?: boolean }>`
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'grab')};
  display: block;
`

const defaultFormatter = (value: string) => `${value}`

interface RangeThumbProps {
  disabled?: boolean
  formatter: (value: string) => string
  mainColor: string
  portalRoot?: HTMLElement | null
  showTooltip?: boolean
  showValue: boolean
  styles: ReturnType<typeof useStyleConfig>
  val: number
}

const RangeThumb = ({ disabled, formatter, mainColor, portalRoot, showTooltip, showValue, styles, val }: RangeThumbProps) => {
  const formattedVal = formatter(String(val))

  const thumbContent = (
    <SliderThumb $disabled={disabled} className="vui-range-thumb" {...styles.thumb} borderColor={mainColor}>
      {showValue && (
        <Box center className="vui-range-thumb-value" {...styles.thumbValue}>
          {formattedVal}
        </Box>
      )}
    </SliderThumb>
  )

  const radixThumb = <RadixSlider.Thumb asChild>{thumbContent}</RadixSlider.Thumb>

  if (showTooltip === false || (showTooltip === undefined && showValue)) return radixThumb

  return (
    <Tooltip disabled={disabled} text={formattedVal} visible={showTooltip === true} portalRoot={portalRoot}>
      {radixThumb}
    </Tooltip>
  )
}

const toArray = (val: number | readonly number[] | undefined): number[] | undefined => {
  if (val === undefined) return undefined
  return Array.isArray(val) ? Array.from(val as readonly number[]) : [val as number]
}

/** Implements a range input. */
export const Range = vui<'div', RangeProps>((props, ref) => {
  const {
    className,
    value,
    defaultValue = 0,
    disabled,
    min = 0,
    minDistance = 0,
    max = 100,
    step = 1,
    showTooltip,
    showValue = false,
    formatter = defaultFormatter,
    onChange,
    ...rest
  } = props

  const styles = useStyleConfig('Range', props)
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null)
  const aliasedProps = filterUndefined({
    'aria-disabled': disabled,
  })
  const isSingleValue = typeof defaultValue !== 'object' && typeof value !== 'object'
  const mainColor = disabled ? rangeColors.disabled : rangeColors.main

  const defaultArr = useMemo(() => toArray(defaultValue) ?? [0], [defaultValue])
  const valueArr = toArray(value)

  const [internalValues, setInternalValues] = useState<number[]>(defaultArr)
  const currentValues = valueArr ?? internalValues

  const handleValueChange = (vals: number[]) => {
    if (!valueArr) setInternalValues(vals)
    onChange?.(isSingleValue ? vals[0] : vals)
  }

  return (
    <Box
      className={cs('vui-range', isSingleValue ? 'vui-range-single' : 'vui-range-range', className)}
      ref={(el: HTMLDivElement | null) => {
        setContainerEl(el)
        if (typeof ref === 'function') ref(el)
          else if (ref) (ref as React.RefObject<unknown>).current = el
      }}
      {...styles.container}
      {...aliasedProps}
      {...rest}
    >
      <RadixSlider.Root
        asChild
        disabled={disabled}
        max={max}
        min={min}
        minStepsBetweenThumbs={minDistance}
        onValueChange={handleValueChange}
        step={step}
        {...(valueArr ? { value: valueArr } : { defaultValue: defaultArr })}
      >
        <SliderRoot $disabled={disabled} className="vui-range-slider">
          <RadixSlider.Track asChild>
            <SliderTrack bg={rangeColors.track} borderRadius="24px" className="vui-range-track">
              <RadixSlider.Range asChild>
                <SliderRange {...styles.track} bg={mainColor} />
              </RadixSlider.Range>
            </SliderTrack>
          </RadixSlider.Track>
          {currentValues.map((val, index) => (
            <RangeThumb
              key={index}
              disabled={disabled}
              formatter={formatter}
              mainColor={mainColor}
              portalRoot={containerEl}
              showTooltip={showTooltip}
              showValue={showValue}
              styles={styles}
              val={val}
            />
          ))}
        </SliderRoot>
      </RadixSlider.Root>
    </Box>
  )
})

Range.displayName = 'Range'
export default Range
